// Hire theme provider - owns light/dark mode, persists the choice, syncs the
// <html data-theme> attribute (drives tokens.css), and supplies the MUI theme.
// Wrap the whole app once in <AppThemeProvider>; read/toggle via useColorMode().

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { makeTheme } from './theme';
import './tokens.css';

const STORAGE_KEY = 'hireTheme';

const ColorModeContext = createContext({ mode: 'light', toggle: () => {}, setMode: () => {} });

export function useColorMode() {
  return useContext(ColorModeContext);
}

function getInitialMode() {
  if (typeof window === 'undefined') return 'light';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved === 'dark' || saved === 'light' ? saved : 'light';
}

export function AppThemeProvider({ children }) {
  const [mode, setMode] = useState(getInitialMode);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      /* ignore storage failures (private mode etc.) */
    }
  }, [mode]);

  const toggle = useCallback(() => setMode((m) => (m === 'light' ? 'dark' : 'light')), []);

  const theme = useMemo(() => makeTheme(mode), [mode]);
  const ctx = useMemo(() => ({ mode, toggle, setMode }), [mode, toggle]);

  return (
    <ColorModeContext.Provider value={ctx}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default AppThemeProvider;
